import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  Loader2,
  Plus,
  Eye,
  Power,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Briefcase,
} from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "@/lib/api";
import type { Vacancy } from "@shared/types";

export default function AdminVacancies() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [vacancies, setVacancies] = useState<Vacancy[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [deleteTarget, setDeleteTarget] = useState<Vacancy | null>(null);
  const [deleting, setDeleting] = useState(false);

  const status = searchParams.get("status") || "";

  useEffect(() => {
    loadVacancies();
  }, [status, page]);

  const loadVacancies = async () => {
    setLoading(true);
    try {
      const response = await apiClient.admin.vacancies.list({
        status: status || undefined,
        page: String(page),
        pageSize: "20",
      });
      setVacancies(response.data as Vacancy[]);
      setTotal(response.total);
      setTotalPages(response.totalPages);
    } catch (error) {
      console.error("Failed to load vacancies:", error);
      toast.error("Failed to load vacancies");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusFilter = (value: string) => {
    if (value === "all") {
      searchParams.delete("status");
    } else {
      searchParams.set("status", value);
    }
    setPage(1);
    setSearchParams(searchParams);
  };

  const handleToggleActive = async (vacancy: Vacancy) => {
    try {
      await apiClient.admin.vacancies.update(vacancy.id, {
        isActive: !vacancy.isActive,
      });
      setVacancies((prev) =>
        prev.map((v) =>
          v.id === vacancy.id ? { ...v, isActive: !v.isActive } : v
        )
      );
      toast.success(
        vacancy.isActive ? "Vacancy deactivated" : "Vacancy activated"
      );
    } catch (error) {
      console.error("Failed to toggle vacancy:", error);
      toast.error("Failed to update vacancy");
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    setDeleting(true);
    try {
      await apiClient.admin.vacancies.delete(deleteTarget.id);
      setVacancies((prev) => prev.filter((v) => v.id !== deleteTarget.id));
      setTotal((t) => t - 1);
      toast.success("Vacancy deleted");
      setDeleteTarget(null);
    } catch (error) {
      console.error("Failed to delete vacancy:", error);
      toast.error("Failed to delete vacancy");
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-6 border-b border-border/50">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Vacancies</h1>
          <p className="text-muted-foreground">
            {total} total vacanc{total !== 1 ? "ies" : "y"}
          </p>
        </div>
        <Link to="/admin/vacancies/new">
          <Button>
            <Plus className="mr-2 h-4 w-4" />
            Create Vacancy
          </Button>
        </Link>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <CardTitle>All Vacancies</CardTitle>
            <Select
              value={status || "all"}
              onValueChange={handleStatusFilter}
            >
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : vacancies.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="p-3 rounded-full bg-muted mb-4">
                <Briefcase className="h-6 w-6 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold">No vacancies found</h3>
              <p className="text-sm text-muted-foreground max-w-sm text-center mt-1">
                {status
                  ? `There are no vacancies with "${status}" status. Try a different filter.`
                  : "Create your first vacancy to start attracting candidates."}
              </p>
              {!status && (
                <Link to="/admin/vacancies/new" className="mt-4">
                  <Button variant="outline">
                    <Plus className="mr-2 h-4 w-4" />
                    Create your first vacancy
                  </Button>
                </Link>
              )}
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Title</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Pay Rate</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {vacancies.map((vacancy) => (
                      <TableRow key={vacancy.id} className="transition-colors hover:bg-muted/50">
                        <TableCell>
                          <div>
                            <p className="font-medium">{vacancy.title}</p>
                            {vacancy.titleEs && (
                              <p className="text-sm text-muted-foreground">
                                {vacancy.titleEs}
                              </p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{vacancy.location || "N/A"}</TableCell>
                        <TableCell>{vacancy.payRate || "N/A"}</TableCell>
                        <TableCell>
                          <Badge
                            className={
                              vacancy.isActive
                                ? "bg-green-500 text-white"
                                : "bg-gray-500 text-white"
                            }
                          >
                            {vacancy.isActive ? "Active" : "Inactive"}
                          </Badge>
                        </TableCell>
                        <TableCell>{formatDate(vacancy.createdAt)}</TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-1">
                            <Link to={`/admin/vacancies/${vacancy.id}`}>
                              <Button size="sm" variant="ghost" title="View">
                                <Eye className="h-4 w-4" />
                              </Button>
                            </Link>
                            <Button
                              size="sm"
                              variant="ghost"
                              title={vacancy.isActive ? "Deactivate" : "Activate"}
                              onClick={() => handleToggleActive(vacancy)}
                            >
                              <Power
                                className={`h-4 w-4 ${
                                  vacancy.isActive
                                    ? "text-green-500"
                                    : "text-muted-foreground"
                                }`}
                              />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              title="Delete"
                              onClick={() => setDeleteTarget(vacancy)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Mobile cards */}
              <div className="md:hidden space-y-4">
                {vacancies.map((vacancy) => (
                  <div
                    key={vacancy.id}
                    className="p-4 border rounded-lg space-y-3 transition-all duration-200 hover:border-primary/30"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium">{vacancy.title}</p>
                        {vacancy.titleEs && (
                          <p className="text-sm text-muted-foreground">
                            {vacancy.titleEs}
                          </p>
                        )}
                      </div>
                      <Badge
                        className={
                          vacancy.isActive
                            ? "bg-green-500 text-white"
                            : "bg-gray-500 text-white"
                        }
                      >
                        {vacancy.isActive ? "Active" : "Inactive"}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      {vacancy.location && <span>{vacancy.location}</span>}
                      {vacancy.payRate && <span>{vacancy.payRate}</span>}
                      <span>{formatDate(vacancy.createdAt)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Link to={`/admin/vacancies/${vacancy.id}`} className="flex-1">
                        <Button size="sm" variant="outline" className="w-full">
                          <Eye className="mr-2 h-4 w-4" />
                          View
                        </Button>
                      </Link>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleToggleActive(vacancy)}
                      >
                        <Power className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setDeleteTarget(vacancy)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">
                    Page {page} of {totalPages}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Vacancy</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deleteTarget?.title}"? This will
              deactivate the vacancy.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
